// subpath import: the full engine loads only with the play screens
import { toSpeechVi } from "@mathbee/engine/speech";
import {
  useContext,
  useEffect,
  useState,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type ReactNode,
} from "react";
import type { Outfit } from "@mathbee/engine/shop";
import type { Avatar } from "../api/types.ts";
import { assetSrc } from "../art/assets.ts";
import { Bee } from "../art/Bee.tsx";
import { OutfitContext } from "../art/outfitContext.ts";
import { copy } from "../copy/index.ts";
import { sound, starDelay, useSoundOn } from "../lib/sound.ts";
import styles from "./ui.module.css";

type Variant = "primary" | "secondary" | "ghost" | "success" | "danger";

export function Button({
  variant = "primary",
  size = "m",
  block = false,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: "s" | "m" | "l";
  block?: boolean;
}) {
  return (
    <button
      type="button"
      className={`${styles.btn} ${styles[variant]} ${styles[`size-${size}`]} ${block ? styles.block : ""} ${className ?? ""}`}
      {...rest}
    >
      {children}
    </button>
  );
}

/** a link that looks like a Button (file downloads) */
export function LinkButton({
  variant = "primary",
  size = "m",
  className,
  children,
  ...rest
}: AnchorHTMLAttributes<HTMLAnchorElement> & { variant?: Variant; size?: "s" | "m" | "l" }) {
  return (
    <a
      className={`${styles.btn} ${styles[variant]} ${styles[`size-${size}`]} ${className ?? ""}`}
      {...rest}
    >
      {children}
    </a>
  );
}

export function Icon({ name, size = 28 }: { name: string; size?: number }) {
  const src = assetSrc(name);
  return src ? (
    <img src={src} width={size} height={size} alt="" className={styles.icon} draggable={false} />
  ) : null;
}

export function StatPill({
  icon,
  value,
  label,
}: {
  icon: string;
  value: ReactNode;
  label: string;
}) {
  return (
    <span className={styles.pill} title={label}>
      <Icon name={icon} size={26} />
      <strong>{value}</strong>
      <span className="sr-only">{label}</span>
    </span>
  );
}

export function Stars({
  value,
  max = 3,
  size = 28,
  pop = false,
}: {
  value: number;
  max?: number;
  size?: number;
  /** results screen: earned stars pop in one by one, in time with their "ting" */
  pop?: boolean;
}) {
  return (
    <span className={styles.stars} role="img" aria-label={`${value} trên ${max} sao`}>
      {Array.from({ length: max }, (_, i) => (
        <img
          key={i}
          src={assetSrc("star") ?? ""}
          width={size}
          height={size}
          alt=""
          className={i < value ? `${styles.starOn} ${pop ? styles.starPop : ""}` : styles.starOff}
          style={pop && i < value ? { animationDelay: `${starDelay(i)}s` } : undefined}
          draggable={false}
        />
      ))}
    </span>
  );
}

export function ProgressBar({
  value,
  max,
  tone = "sky",
}: {
  value: number;
  max: number;
  tone?: "sky" | "leaf" | "honey";
}) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div
      className={styles.progress}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value}
    >
      <div
        className={`${styles.progressFill} ${styles[`tone-${tone}`]}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

const AVATAR_BG: Record<string, string> = {
  honey: "#fff3c4",
  sky: "#d6ebff",
  leaf: "#dcf5e1",
  grape: "#ebe4ff",
  coral: "#ffe2d9",
};

/** A child's bee; `outfit` omitted = the active child's outfit (child mode, SPEC 7.5). */
export function ChildAvatar({
  avatar,
  outfit,
  size = 56,
}: {
  avatar: Avatar;
  outfit?: Outfit | undefined;
  size?: number;
}) {
  const childLook = useContext(OutfitContext);
  const frame = (outfit ?? childLook).frame;
  const face = (
    <span
      className={styles.avatar}
      style={{
        width: size,
        height: size,
        background: AVATAR_BG[avatar.color ?? "honey"] ?? AVATAR_BG.honey,
      }}
    >
      <Bee size={size * 0.9} {...(outfit ? { outfit } : {})} animate={false} />
    </span>
  );
  if (!frame) return face;
  // avatar frame from the shop (T5.9b): a ring around the bee, some with little decorations
  return (
    <span
      className={`${styles.frame} ${styles[frame] ?? ""}`}
      style={
        {
          padding: Math.max(3, Math.round(size * 0.06)),
          "--deco": `${Math.round(size * 0.3)}px`,
        } as CSSProperties
      }
    >
      {face}
    </span>
  );
}

/** Large on-screen number pad; `extra` adds operator/comparator keys for non-numeric blanks. */
export function NumPad({
  onKey,
  extra,
  disabled,
}: {
  onKey: (key: string) => void;
  extra?: readonly string[];
  disabled?: boolean;
}) {
  const keys = extra?.length ? [...extra] : ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];
  return (
    <div
      className={`${styles.numpad} ${extra?.length ? styles.numpadOps : ""}`}
      role="group"
      aria-label="Bàn phím số"
    >
      {keys.map((k) => (
        <button
          key={k}
          type="button"
          className={styles.key}
          onClick={() => onKey(k)}
          disabled={disabled}
        >
          {k === "-" ? "–" : k}
        </button>
      ))}
      <button
        type="button"
        className={`${styles.key} ${styles.keyDel}`}
        onClick={() => onKey("⌫")}
        disabled={disabled}
        aria-label="Xoá"
      >
        ⌫
      </button>
    </div>
  );
}

export type SlotState = "idle" | "ok" | "bad";

export function Slot({
  value,
  focused,
  state = "idle",
  onClick,
  wide = false,
  label,
}: {
  value: string;
  focused: boolean;
  state?: SlotState;
  onClick: () => void;
  wide?: boolean;
  label?: string;
}) {
  return (
    <button
      type="button"
      className={`${styles.slot} ${focused ? styles.slotFocus : ""} ${styles[`slot-${state}`]} ${wide ? styles.slotWide : ""}`}
      onClick={onClick}
      aria-label={label ?? (value ? `Ô trống: ${value}` : "Ô trống")}
    >
      {value === "-" ? "–" : value || " "}
    </button>
  );
}

/** Bottom feedback sheet (C8). */
export function Sheet({
  tone,
  children,
}: {
  tone: "success" | "retry" | "info";
  children: ReactNode;
}) {
  return (
    <div className={`${styles.sheet} ${styles[`sheet-${tone}`]}`} role="status" aria-live="polite">
      {children}
    </div>
  );
}

/** Read text aloud. M1 uses the browser voice when a Vietnamese one exists; Piper TTS comes in T3.11/T4.7. */
export function Speak({ text }: { text: string }) {
  const [voice, setVoice] = useState<SpeechSynthesisVoice | null>(null);
  useEffect(() => {
    if (typeof speechSynthesis === "undefined") return;
    const pick = () =>
      setVoice(
        speechSynthesis.getVoices().find((v) => v.lang.toLowerCase().startsWith("vi")) ?? null,
      );
    pick();
    speechSynthesis.addEventListener("voiceschanged", pick);
    return () => speechSynthesis.removeEventListener("voiceschanged", pick);
  }, []);
  if (!voice) return null;
  return (
    <button
      type="button"
      className={styles.speak}
      aria-label="Đọc to"
      onClick={() => {
        speechSynthesis.cancel();
        const u = new SpeechSynthesisUtterance(toSpeechVi(text));
        u.voice = voice;
        u.lang = voice.lang;
        u.rate = 0.9;
        speechSynthesis.speak(u);
      }}
    >
      <Icon name="speaker" size={28} />
    </button>
  );
}

/**
 * The child's sound-effects button (SPEC 9.6): a bell, so it is not confused with the speaker of "Đọc to".
 * Overrides the parent's default on this device.
 */
export function SoundToggle() {
  const on = useSoundOn();
  const label = on ? copy.sound.turnOff : copy.sound.turnOn;
  return (
    <button
      type="button"
      className={`${styles.soundToggle} ${on ? "" : styles.soundOff}`}
      aria-pressed={on}
      aria-label={label}
      title={label}
      onClick={() => sound.toggle()}
    >
      <Icon name={on ? "bell" : "bell_off"} size={28} />
    </button>
  );
}

export function Bubble({ children }: { children: ReactNode }) {
  return <div className={styles.bubble}>{children}</div>;
}

export function Spinner() {
  return (
    <div className={styles.spinner} role="status" aria-label="Đang tải">
      <Bee size={90} expression="think" />
    </div>
  );
}
